import { Button, Typography } from '@material-ui/core';
import Assistant from '@material-ui/icons/Assistant';

import {
  DialogComposite,
  MultiStepDialog,
  StringUtils,
  useFormField,
  useMultiStepDialogContext,
} from '@spotify/confidence-core-react';

import { AboutFormFields, ResourceType } from '../forms';
import { MetricFormValues, StepId } from '../types';

type AboutStepProps = {
  stepId?: string;
  showGenerate?: boolean;
  onGenerate?: () => void;
  generating?: boolean;
  resourceType?: ResourceType;
};

export const AboutStep = ({
  stepId = StepId.About,
  showGenerate = false,
  onGenerate,
  generating = false,
  resourceType = 'metric',
}: AboutStepProps) => {
  const { activeStep } = useMultiStepDialogContext();
  const isFirstStep = activeStep === 0;
  const { value: displayName } = useFormField<MetricFormValues, 'displayName'>(
    'displayName',
  );
  const { value: owner } = useFormField<MetricFormValues, 'owner'>('owner');
  const { value: description } = useFormField<MetricFormValues, 'description'>(
    'description',
  );

  const canProceed = Boolean(displayName && owner);

  // Compute canGenerate reactively from form field values
  // Either a meaningful name OR a descriptive description is enough
  const canGenerate: boolean =
    Boolean(displayName && displayName.length > 5) ||
    Boolean(description && StringUtils.countWords(description) >= 5);

  return (
    <MultiStepDialog.Step id={stepId}>
      <MultiStepDialog.Header>
        <Typography variant="h4" component="h4">
          About
        </Typography>
        <DialogComposite.Subtitle>
          Set a name and description for your {resourceType}. You can change
          them later.
        </DialogComposite.Subtitle>
      </MultiStepDialog.Header>
      <MultiStepDialog.Body>
        <AboutFormFields resourceType={resourceType} />
      </MultiStepDialog.Body>
      <MultiStepDialog.Actions>
        {isFirstStep ? (
          <MultiStepDialog.Actions.Cancel />
        ) : (
          <MultiStepDialog.Actions.Prev />
        )}
        {showGenerate && (
          <Button
            variant="contained"
            color="primary"
            disabled={!canGenerate || generating}
            onClick={onGenerate}
            startIcon={<Assistant />}
            data-testid="generate-metric-button"
          >
            {generating ? 'Generating...' : 'Generate with AI'}
          </Button>
        )}
        <MultiStepDialog.Actions.Next disabled={!canProceed} />
      </MultiStepDialog.Actions>
    </MultiStepDialog.Step>
  );
};
